/**
 * Die Worte der Glocke — aus der Sprache der Maschine in einfache Sätze. (07.10.2026)
 *
 * Martin, 07.10.: *„Das ist unbedienbar … ich weiß ehrlich gesagt nicht, was ich
 * mit der Liste anfangen soll.“* In seinen Zeilen standen „Hintergrund-Auftrag“,
 * „Auftrag 8343 auf Webseite 23“ und „Ticket #2875“ — Wörter, die er nie
 * benutzt. Hier wird aus Quelle, Titel und Zusammenfassung eines Hinweises ein
 * Titel und EIN Satz dazu. Reine Funktionen: keine Uhr, kein DOM, kein Server —
 * der Server ruft sie beim Ordnen (shared/glocke-ordnung.ts), die Tests mit den
 * echten Zeilen von Martins Bild.
 *
 * ⚠️ Die Gruppen (wer „braucht dich“, wer „fertig“ ist) entscheidet NUR
 *    glocke-ordnung.ts. Dieses Modul übersetzt, es ordnet nichts — sonst gäbe
 *    es zwei Stellen, die über dieselbe Zeile urteilen. Die Ausnahme sind die
 *    Muster, nach denen entschieden wird (`braucheVorschau`, `aufnahmeHinweis`,
 *    `anrufAnlass`): sie stehen HIER, weil Titel und Satz dieselbe Erkennung
 *    brauchen — die Ordnung ruft sie nur.
 */
import { ohneLaufMarker } from "./html-text.js";
import { satzEnden } from "./satzgrenzen.js";
import { wortGrenze } from "./wortgrenze.js";

/** Die Laufkennung „(#f2d2b9da)“ am Ende — für Martin ohne Wert. */
const LAUF_KENNUNG = /\s*\(#[0-9a-f]{8}\)/gi;
/** „— Ticket #2447.“, „Ticket 2725“: eine Nummer aus dem Ticketsystem, kein Wort für Martin. */
const TICKET_BEZUG = /\s*(?:[—–]\s*)?Ticket\s*#?\d+\b\.?/gi;
/** „Nachzusehen unter /wartet#pruefstand.“: ein Pfad, den Martin nie tippt — die Zeile trägt dafür den Knopf „Ansehen“. */
const PFAD_HINWEIS = /\s*(?:[—–]\s*)?Nachzusehen unter \/\S+/gi;
/** „(#2967)“ hinter einem Titel: dieselbe Nummer in Klammern. */
const NUMMER_IN_KLAMMER = /\s*\(#\d+\)/g;
/** Fettdruck der Maschinenberichte. */
const FETT = /\*\*/g;

/**
 * „Hintergrundlauf“ und „Hintergrund-Auftrag“ sind Wörter der Technik. Martin sagt „Auftrag“ — und die Beugung
 * bleibt, weil beide männlich sind („des Hintergrundlaufs“ → „des Auftrags“, „Hintergrundläufe“ → „Aufträge“).
 */
const HINTERGRUND = /\bHintergrund-?(?:lauf|läufe|auftrag|aufträge)(es|s|en|n)?\b/gi;

/** Setzt die einfachen Worte für die Wörter der Technik ein. */
function einfacheWorte(text: string): string {
  return text.replace(HINTERGRUND, (treffer, ende: string | undefined) => {
    const mehrzahl = /(?:äufe|äge)/i.test(treffer);
    return (mehrzahl ? "Aufträge" : "Auftrag") + (ende ? ende.toLowerCase() : "");
  });
}

/** Das „· 06.10.“ am Ende, mit dem die wiederkehrenden Läufe ihren Tag tragen — die Zeit steht klein darunter. */
const TAG_AM_ENDE = /\s*·\s*\d{1,2}\.\d{1,2}\.(?:\d{2,4})?\s*$/;

/** Ein Titel ohne das Datum dahinter. */
export function ohneTagAmEnde(title: string): string {
  return title.replace(TAG_AM_ENDE, "").trim();
}

/** Hinweistext ohne Laufkennung, Ticketnummer, Lauf-Marker (TREFFER:) und Fettdruck. */
export function bereinige(text: string): string {
  return ohneLaufMarker(text)
    .replace(LAUF_KENNUNG, "")
    .replace(TICKET_BEZUG, "")
    .replace(PFAD_HINWEIS, "")
    .replace(NUMMER_IN_KLAMMER, "")
    .replace(FETT, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Wörter, mit denen ein Satz nicht enden soll: „… eine Rechnungsregel oder“. */
const HAENGT = new Set([
  "und", "oder", "aber", "sowie", "der", "die", "das", "den", "dem", "des", "ein", "eine", "einen", "einem", "einer",
  "zu", "mit", "für", "von", "in", "im", "an", "am", "auf", "bei", "nach", "wenn", "dass", "weil", "als", "wie",
]);

/** Schneidet an einer Wortgrenze: nie mitten im Wort, nie mit hängendem „und“, und nur dann mit „…“. */
function kuerzeAmWort(text: string, max: number, mitteImWort = false): string {
  let stueck = text.length > max ? text.slice(0, max + 1) : text;
  // Das letzte Wort ist vielleicht angeschnitten (Schnitt mitten im Text oder gespeicherter Abbruch).
  if (text.length > max || mitteImWort) stueck = stueck.slice(0, Math.max(0, stueck.lastIndexOf(" ")));
  // Lieber an einem Gedankenstrich enden, wenn davor noch ein halbwegs voller Satz steht.
  const strich = Math.max(stueck.lastIndexOf(" — "), stueck.lastIndexOf(" – "));
  if (strich >= max * 0.4) return `${stueck.slice(0, strich).replace(/[,;:\s]+$/, "")}.`;
  const worte = stueck.split(" ");
  while (worte.length > 1 && HAENGT.has((worte[worte.length - 1] ?? "").toLowerCase().replace(/[,;:]+$/, ""))) worte.pop();
  const rumpf = worte.join(" ").replace(/[,;:\s]+$/, "");
  return rumpf ? `${rumpf}…` : "";
}

/** Zerlegt in Sätze — an den Grenzen, die shared/satzgrenzen.ts kennt („18. September“, „z. B.“ trennen nicht). */
function zerlege(t: string): string[] {
  const saetze: string[] = [];
  let start = 0;
  for (const ende of satzEnden(t)) {
    saetze.push(t.slice(start, ende).trim());
    start = ende;
  }
  const rest = t.slice(start).trim();
  if (rest) saetze.push(rest);
  return saetze.filter(Boolean);
}

/**
 * Die ersten `n` Sätze, zusammen höchstens `max` Zeichen — gekürzt an einer
 * Wortgrenze, nie mitten im Wort. Ein gespeicherter Abbruch („… oder K…“) verliert
 * sein angeschnittenes Wort. Leer, wenn nichts da ist.
 */
export function saetze(text: string, n: number, max: number): string {
  let t = bereinige(text);
  if (!t) return "";
  // Die Ablage kappt bei 300 Zeichen und setzt „…“ — das letzte Wort davor ist angeschnitten.
  const abgebrochen = /(…|\.{3})$/.test(t);
  t = t.replace(/(…|\.{3})$/, "").trimEnd();
  const teile = zerlege(t);
  const gewaehlt: string[] = [];
  let laenge = 0;
  for (const s of teile.slice(0, n)) {
    if (gewaehlt.length > 0 && laenge + 1 + s.length > max) break;
    gewaehlt.push(s);
    laenge += (gewaehlt.length > 1 ? 1 : 0) + s.length;
  }
  const letzterIstEnde = gewaehlt.length === teile.length;
  const roh = gewaehlt.join(" ");
  if (roh.length > max) return kuerzeAmWort(roh, max);
  if (abgebrochen && letzterIstEnde && !/[.!?]$/.test(roh)) return kuerzeAmWort(roh, max, true);
  return roh;
}

/** Was eine Zeile der Karte sagt: ein Titel und, wenn es einen gibt, EIN Satz dazu. */
interface GlockenWorte {
  titel: string;
  text: string;
}

/** Bei der Quelle „panel“: die Webseite, der Ausgang. „Auftrag 8343 auf Webseite 23: fertig“. */
const WEBSEITE = /^Auftrag \d+ auf (.+?): (fertig|gescheitert|dauert ungewöhnlich lange)$/;
const ANRUF = /^Vespera hat versucht anzurufen(?::\s*(.*))?$/;
const AUFNAHME = /^Aufnahme (?:unvollständig|(\d{1,2}:\d{2}): nur (\d+) von (\d+) Minuten)/;
const TASK = /^(?:Task|Hintergrund-Auftrag)(?: (fertig|fehlgeschlagen|abgeschlossen))?(?: · (.+?))?(?: \[Mac\])?$/;

/**
 * Die Sätze, mit denen die Schreiber um den Blick auf die Vorschau bitten — EINE Stelle für Schreiber und Leser: Wer
 * den Wortlaut ändert, ändert ihn hier, und `braucheVorschau` erkennt ihn weiter (tests/server/glocke-vorschau-schreiber.test.ts).
 * Eine Umformulierung an den Schreibern vorbei kostete still den Punkt an der Glocke.
 *   VORSCHAU_SATZ  server/panel-wache.ts (der Wächter, der einen fertigen Auftrag meldet)
 *   VORSCHAU_KURZ  tools/webseiten.ts (die Antwort des Werkzeugs, die sich selbst in die Glocke legt)
 */
export const VORSCHAU_SATZ = "Schau dir die Vorschau an, bevor etwas live geht.";
export const VORSCHAU_KURZ = "Schau dir die Vorschau an.";

/**
 * Der Satz, der eine HANDLUNG verlangt: „Schau dir die Vorschau an, bevor etwas live geht.“ — und seine Verwandten
 * („Schau dir bitte die Vorschau an“, „Bitte die Vorschau ansehen“, „Vorschau prüfen“). Ohne ihn ist die Meldung nur
 * die Nachricht, dass etwas fertig ist. Unicode-feste Wortgrenzen (`\b` kennt kein „ü“: „prüf…“).
 */
const FUELLER = "(?:dir|bitte|noch|mal|einmal|kurz|doch|auch)";
const VORSCHAU_PRUEFEN = wortGrenze(
  `(?:schau|sieh|prüf|guck|kontrollier)\\p{L}*\\s+(?:${FUELLER}\\s+){0,3}(?:die\\s+)?Vorschau` +
    `|(?:die\\s+)?Vorschau\\s+(?:${FUELLER}\\s+){0,2}(?:an|ansehen|anschauen|prüfen|kontrollieren|durchsehen|checken)`,
);

/** Ist das der Hinweis „Aufnahme unvollständig“ (in beiden Fassungen des Titels)? */
export function aufnahmeHinweis(title: string): boolean {
  return AUFNAHME.test(bereinige(title));
}

/** „Webseite 23“ oder die Adresse, wie das Panel sie nennt — für „Webseite 23: Änderung fertig“. */
export function webseiteVon(title: string): string | null {
  return WEBSEITE.exec(bereinige(title))?.[1] ?? null;
}

/**
 * Ist das ein FERTIGER Webseiten-Auftrag, bei dem Martin die Vorschau prüfen soll? Dann braucht ihn die Meldung —
 * die Ordnung stellt ihn zu „Braucht dich“ (Prüfbefund 07.10.: Ticket 2331 sagt „Panel-Jobs werden fertig, ohne dass
 * ich es erfahre“, und „bevor etwas live geht“ ist eine Handlung). Ohne den Handlungssatz bleibt er „Fertig“.
 */
export function braucheVorschau(e: { source: string; title: string; summary: string }): boolean {
  if (e.source !== "panel") return false;
  const web = WEBSEITE.exec(bereinige(e.title));
  return web?.[2] === "fertig" && VORSCHAU_PRUEFEN.test(bereinige(e.summary));
}

/**
 * Der Anlass eines verpassten Anrufs: „Vespera hat versucht anzurufen: Testanruf“ → „Testanruf“.
 * Leer, wenn der Titel keinen trägt; null, wenn es kein Anruf-Titel ist. Gleiche Anlässe sind EIN Bündel,
 * verschiedene nie (glocke-ordnung.ts).
 */
export function anrufAnlass(title: string): string | null {
  const a = ANRUF.exec(ohneTagAmEnde(bereinige(title)));
  return a ? (a[1] ?? "").trim() : null;
}

/** Wie die Webseite in einem Titel steht — immer mit dem Wort davor: „Webseite 23“ trägt es schon, eine Adresse bekommt es („Webseite beispiel.at“). */
export function webseitenName(w: string): string {
  return /^Webseite\b/.test(w) ? w : `Webseite ${w}`;
}

/** Was Vespera in die Selbstverbesserung schreibt, ist ihre eigene Stimme: „Ich habe …“. */
const ICH_FORM = /^(?:Ich|Wir)\b/;
const NICHTS_GEAENDERT = /\bnichts\s+(?:mehr\s+|weiter\s+)?(?:zu\s+)?(?:(?:ge)?änder|nötig|zu tun)/i;
// Unicode-feste Grenzen: „überprüft“ beginnt mit einem Umlaut, `\b` davor trifft nie.
const PRUEF = "geprüft|überprüft|nachgeprüft|nachgemessen|gemessen|getestet|nachgesehen|nachgeschaut";
const BAU =
  "gebaut|eingebaut|eingespielt|behoben|vorbereitet|geändert|ergänzt|umgestellt|repariert|korrigiert|angepasst|verbessert|entfernt|aufgeräumt|eingerichtet|hinzugefügt|umgebaut|ausgetauscht|gesichert";
const PRUEFWORT = wortGrenze(PRUEF);
const BAUWORT = wortGrenze(BAU);
const ERSTES_WORT = wortGrenze(`${PRUEF}|${BAU}|geschrieben`);

/**
 * Der Titel einer fertigen Selbstverbesserung. Steht im Satz „Ich habe …“, trägt der Titel die ART der Arbeit
 * („Plan geschrieben“, „Prüfung fertig“, „Nichts zu ändern“, „Verbesserung eingebaut“, sonst „Abschluss gemeldet“) —
 * so sind die Zeilen unterscheidbar, und unter einem Titel in der dritten Person steht nie ein Satz in der Ich-Form.
 * Alles andere (der Satz beschreibt, was Vespera jetzt kann) bleibt „Vespera hat etwas Neues gelernt“.
 */
function titelDerSelbstverbesserung(satz: string): string {
  const erster = zerlege(bereinige(satz))[0] ?? "";
  if (!ICH_FORM.test(erster)) return "Vespera hat etwas Neues gelernt";
  if (NICHTS_GEAENDERT.test(erster)) return "Nichts zu ändern";
  const erstes = ERSTES_WORT.exec(erster)?.[0]?.toLowerCase();
  if (erstes === "geschrieben") return /\bPlan\b/i.test(erster) ? "Plan geschrieben" : "Abschluss gemeldet";
  if (erstes && PRUEFWORT.test(erstes)) return "Prüfung fertig";
  if (erstes && BAUWORT.test(erstes)) return "Verbesserung eingebaut";
  return "Abschluss gemeldet";
}

/**
 * Ein Hinweis in Martins Worten.
 *
 * Titel und Satz kommen aus dem, was die Schreiber ablegen (recordHandover) —
 * die Muster sind am Bestand vom 07.10. abgelesen. Was keins trifft, bleibt
 * wie es ist, nur ohne Datum, Laufkennung und Ticketnummer: ein Hinweis, den
 * Vespera selbst hinterlegt (glocke_hinterlegen), ist kein Rauschen.
 */
export function uebersetze(e: { source: string; title: string; summary: string }): GlockenWorte {
  const w = uebersetzeRoh(e);
  return { titel: einfacheWorte(w.titel), text: einfacheWorte(w.text) };
}

function uebersetzeRoh(e: { source: string; title: string; summary: string }): GlockenWorte {
  const titel = ohneTagAmEnde(bereinige(e.title));

  if (e.source === "anruf") {
    const a = ANRUF.exec(titel);
    if (a) return { titel: "Vespera hat vergeblich angerufen", text: saetze(a[1] ?? "", 1, 110) };
  }

  const aufnahme = AUFNAHME.exec(titel);
  if (e.source === "system" && aufnahme) {
    // Frühere Zeilen trugen die Zahlen im Titel („Aufnahme 22:11: nur 11 von 19 Minuten“), die neuen im Satz.
    const zahlen = aufnahme[1] ? `Aufnahme ${aufnahme[1]}: nur ${aufnahme[2]} von ${aufnahme[3]} Minuten.` : "";
    return { titel: "Eine Aufnahme war unvollständig", text: saetze(`${zahlen} ${e.summary}`, 2, 150) };
  }

  const web = WEBSEITE.exec(titel);
  if (e.source === "panel" && web) {
    if (braucheVorschau(e)) return { titel: `${webseitenName(web[1] ?? "")}: Vorschau ansehen`, text: saetze(e.summary, 2, 140) };
    const wort = { fertig: "Änderung fertig", gescheitert: "Änderung nicht gelungen" }[web[2] ?? ""] ?? "dauert ungewöhnlich lange";
    return { titel: `${webseitenName(web[1] ?? "")}: ${wort}`, text: saetze(e.summary, 2, 140) };
  }

  if (e.source === "auftrag") {
    if (/^Selbstverbesserung fertig/.test(titel)) return { titel: titelDerSelbstverbesserung(e.summary), text: saetze(e.summary, 1, 120) };
    if (/^Selbstverbesserung fehlgeschlagen/.test(titel)) return { titel: "Eine Verbesserung ist nicht gelungen", text: saetze(e.summary, 1, 120) };
    if (/^Wochenkontingent aufgebraucht/.test(titel)) {
      const satz = bereinige(e.summary).replace(/^Der Auftrag \(.*?\) konnte/, "Eine Aufgabe konnte");
      return { titel: "Das Wochenkontingent ist aufgebraucht", text: saetze(satz, 2, 130) };
    }
    const t = TASK.exec(titel);
    if (t) {
      const wort = t[1] === "fehlgeschlagen" ? "Aufgabe nicht gelungen" : "Aufgabe fertig";
      return { titel: t[2] ? `${wort} · ${t[2]}` : wort, text: saetze(e.summary, 1, 120) };
    }
  }

  return { titel: titel || e.title.trim(), text: saetze(e.summary, 2, 140) };
}

/** Woher Namen und Adressen kommen — beides darf fehlen; dann bleibt der Hinweis, wie er ist. */
export interface Aufloeser {
  /** Der Name des Kontos mit dieser Nummer, wenn der Datensatz einen hat. */
  konto?: (userId: number) => string | null | undefined;
  /** Die Adresse der Webseite mit dieser Nummer, wenn das Panel sie kennt. */
  webseite?: (websiteId: number) => string | null | undefined;
}

/** Der Vorname aus dem Namen des Kontos; eine Mail-Adresse ist kein Name. */
function vornameVon(name: string | null | undefined): string {
  const erstes = (name ?? "").trim().split(/\s+/)[0] ?? "";
  return erstes.includes("@") ? "" : erstes;
}

const KONTO_MELDUNG = /^Konto (\d+)( (?:hat etwas gemeldet|wünscht:).*)$/;
const AUFTRAG_AUF_WEBSEITE = /^(Auftrag \d+ auf )Webseite (\d+)(: .+)$/;

/**
 * Setzt Namen statt „Konto 2“ und Adressen statt „Webseite 23“ ein — NUR, wo der Datensatz sie kennt (Prüfbefund
 * 07.10.). Die Hinweise tragen die Nummer, weil der Schreiber damals keinen Namen hatte; gelesen wird hier, nicht
 * umgeschrieben: die Tabelle bleibt, wie sie ist (und mit ihr der Deckel von `recordHandoverEinmal`).
 */
export function mitNamen<T extends { source: string; title: string }>(e: T, a: Aufloeser): T {
  if (e.source === "nutzer-wunsch" && a.konto) {
    const m = KONTO_MELDUNG.exec(e.title);
    const name = m ? vornameVon(a.konto(Number(m[1]))) : "";
    if (m && name) return { ...e, title: `${name}${m[2]}` };
  }
  if (e.source === "panel" && a.webseite) {
    const m = AUFTRAG_AUF_WEBSEITE.exec(e.title);
    const adresse = m ? a.webseite(Number(m[2]))?.trim() : "";
    if (m && adresse) return { ...e, title: `${m[1]}${adresse}${m[3]}` };
  }
  return e;
}
